// option-availability.js (position-based, robust for any option names)
// Marks NON-EXISTING combos as not-clickable (is-nonexistent)
// and EXISTING-BUT-OUT-OF-STOCK as clickable + note (is-oos).

(() => {
  console.log("[option-availability] init");

  // --- Helpers -------------------------------------------------------------
  const SELECTORS = {
    // all option radios on the product form
    radios: '.product-form__input input[type="radio"]',
    // each option group (fieldset wrapper class used in your theme)
    groups: '.product-form__input',
    // any label that acts as a swatch/chip in a group
    // (covers text chips and round color chips in your theme)
    labels: 'label.product__item-option, label.product__color-swatch, label.swatch-option',
  };

  // escape CSS attr values safely (fallback for older browsers)
  const esc = (s) => {
    if (!s) return s;
    return window.CSS && CSS.escape ? CSS.escape(s) : String(s).replace(/"/g, '\\"');
  };

  const findVariantsJSON = () => {
    // Your theme renders: <script type="application/json" id="Variants-{{ section.id }}">
    const el = document.querySelector('script[id^="Variants-"]');
    if (!el) return null;
    try {
      const arr = JSON.parse(el.textContent || "[]");
      return Array.isArray(arr) ? arr : null;
    } catch (e) {
      return null;
    }
  };

  const getOptionGroups = () => {
    // Collect the option groups in the DOM order (first = driver)
    const groups = Array.from(document.querySelectorAll(SELECTORS.groups));
    // Only keep groups that actually contain radio inputs
    return groups
      .map(g => {
        const radios = Array.from(g.querySelectorAll('input[type="radio"]'));
        // Require at least one radio with a name (option name)
        if (!radios.length || !radios[0].name) return null;
        // Some themes wrap the visible chip text inside a label span;
        // we’ll find labels for later marking
        const labels = Array.from(g.querySelectorAll(SELECTORS.labels));
        return { el: g, name: radios[0].name, radios, labels };
      })
      .filter(Boolean);
  };

  const getCurrentSelection = (groups) => {
    const sel = {};
    groups.forEach(g => {
      const checked = g.radios.find(r => r.checked);
      if (checked) sel[g.name] = checked.value;
    });
    return sel;
  };

  // Remove all previous marks/notes in a target group
  const clearMarks = (labels) => {
    labels.forEach(lbl => {
      lbl.classList.remove('is-nonexistent', 'is-oos');
      const note = lbl.querySelector('.stock-note');
      if (note) note.remove();
      // Also re-enable clicks if something was disabled earlier
      lbl.style.pointerEvents = '';
      lbl.style.cursor = '';
    });
  };

  const addNote = (labelEl, text) => {
    // Make sure we only have ONE <small.stock-note> under the label
    let note = labelEl.querySelector('.stock-note');
    if (!note) {
      note = document.createElement('small');
      note.className = 'stock-note';
      labelEl.appendChild(note);
    }
    note.textContent = text;
  };

  // --- Core logic ----------------------------------------------------------
  const variants = findVariantsJSON();
  if (!variants || !variants.length) {
    console.warn("[option-availability] no variants JSON");
    return;
  }

  const groups = getOptionGroups();
  if (groups.length < 2) {
    // Nothing to compute if there’s only one option group
    return;
  }

  // We always drive from the FIRST group and mark the SECOND group
  const driver = groups[0];
  const target = groups[1];

  // Build a stable map from DOM option names to variant option indices
  // We infer the order by comparing a known variant’s option values
  const radioNames = groups.map(g => g.name);            // e.g. ["Size","Color"] or ["Weight","Jig Head Size"]
  const optionIndexOf = (name) => radioNames.indexOf(name) + 1; // -> 1..3 for option1..option3

  const refresh = () => {
    const selection = getCurrentSelection(groups);
    // Must have a selected value for the driver to evaluate the target
    const driverVal = selection[driver.name];
    if (!driverVal) {
      // No driver selection yet → just clear marks in target
      clearMarks(target.labels);
      return;
    }

    // Compute which target values exist and which are in stock
    const targetExists = new Set();
    const targetAvail  = new Set();

    const driverIdx = optionIndexOf(driver.name); // 1..3
    const targIdx   = optionIndexOf(target.name); // 1..3

    variants.forEach(v => {
      // Keep only variants matching the currently selected driver value
      if (v[`option${driverIdx}`] !== driverVal) return;

      const tVal = v[`option${targIdx}`];
      if (!tVal) return;

      targetExists.add(tVal);
      if (v.available) targetAvail.add(tVal);
    });

    // Apply to DOM
    clearMarks(target.labels);

    target.labels.forEach(lbl => {
      // Value can be in data-value or plain text
      const raw = lbl.getAttribute('data-value') || lbl.innerText || lbl.textContent || '';
      const val = raw.trim();

      if (!val) return; // ignore non-value labels

      if (!targetExists.has(val)) {
        // This combination does NOT exist → hide or disable
        lbl.classList.add('is-nonexistent');
        lbl.style.pointerEvents = 'none';
        lbl.style.cursor = 'not-allowed';
      } else if (!targetAvail.has(val)) {
        // Exists but OOS → keep clickable + show note
        lbl.classList.add('is-oos');
        addNote(lbl, 'Out of stock');
        // ensure clickable for “notify me”
        lbl.style.pointerEvents = 'auto';
        lbl.style.cursor = 'pointer';
      }
    });
  };

  // Recompute whenever any radio changes (driver or target)
  document.addEventListener('change', (e) => {
    const t = e.target;
    if (!(t && t.matches(SELECTORS.radios))) return;
    refresh();
  });

  // Initial run
  refresh();
})();